import { Typography } from '@arionbanki/arionui';
import { formatKennitala } from 'helpers';
import type { ProcessesResponse } from 'types';
import type { SortKey } from './Admin.consts';
import { toBucket } from './Admin.helpers';
import type { AdminHook } from './useAdmin';

const SORT_COLUMNS: { key: SortKey; label: string }[] = [
	{ key: 'name', label: 'admin_applicant' },
	{ key: 'emailInitiator', label: 'admin_contact_details' },
	{ key: 'flowType', label: 'admin_flow' },
	{ key: 'processStatus', label: 'processStatus' },
	{ key: 'created', label: 'created' },
	{ key: 'updated', label: 'modified' }
];

type AdminTableRowProps = Pick<AdminHook, 'formatFlow' | 'formatValue' | 'openDrawer' | 't'> & {
	row: ProcessesResponse;
	showGuid: boolean;
};

function AdminTableRow({
	formatFlow,
	formatValue,
	openDrawer,
	row,
	showGuid,
	t
}: Readonly<AdminTableRowProps>) {
	const rowBucket = toBucket(row.processStatus);
	const isInactive = rowBucket === 'expired' || rowBucket === 'cancelled';
	const isLegalEntity = Number.parseInt(row.kennitala.slice(0, 2), 10) > 40;
	return (
		<tr className={isInactive ? 'admin-row admin-row--inactive' : 'admin-row rowhover'}>
			<td>
				<div className="admin-applicant">
					<span className="admin-applicant__name">{row.fullNameInitiator}</span>
					<span className={`admin-entity-badge ${isLegalEntity ? 'admin-entity-badge--company' : ''}`}>
						{isLegalEntity ? t('admin_legal_entities') : t('admin_individuals')}
					</span>
					{showGuid && (
						<span className="admin-row-guid" title={row.agreementProcessId}>
							{row.agreementProcessId}
						</span>
					)}
				</div>
				<div className="admin-meta">
					<span className="admin-meta__label">{isLegalEntity ? t('company_id_no') : t('id_no')}</span>
					{formatKennitala(row.kennitala)}
				</div>
				{row.kennitalaInitiator !== row.kennitala && (
					<div className="admin-meta">
						<span className="admin-meta__label">{t('admin_founder')}</span>
						{formatKennitala(row.kennitalaInitiator)}
					</div>
				)}
			</td>
			<td>
				<div>{row.emailInitiator}</div>
				<div className="admin-meta">{row.phoneInitiator}</div>
			</td>
			<td>{formatFlow(String(row.flowType))}</td>
			<td>
				<span className={`admin-status admin-status--${rowBucket}`}>
					{formatValue(row, 'processStatus', t)}
				</span>
			</td>
			<td>{formatValue(row, 'created', t)}</td>
			<td>{formatValue(row, 'modified', t)}</td>
			<td>
				{!isInactive && (
					<button className="admin-update-btn" onClick={() => openDrawer(row)} type="button">
						{t('Update')}
					</button>
				)}
			</td>
		</tr>
	);
}

type AdminTableProps = Pick<
	AdminHook,
	'arrowFor' | 'formatFlow' | 'formatValue' | 'isLoading' | 'openDrawer' | 't' | 'toggleSort'
> & {
	data: ProcessesResponse[];
	showGuid: boolean;
};

export function AdminTable({
	arrowFor,
	data,
	formatFlow,
	formatValue,
	isLoading,
	openDrawer,
	showGuid,
	t,
	toggleSort
}: Readonly<AdminTableProps>) {
	return (
		<div className="admin-table-wrap mb-5">
			<table className="admin-table">
				<thead>
					<tr>
						{SORT_COLUMNS.map(column => (
							<th key={column.key}>
								<button className="admin-sort" onClick={() => toggleSort(column.key)} type="button">
									{t(column.label)} {arrowFor(column.key)}
								</button>
							</th>
						))}
						<th>{t('Details')}</th>
					</tr>
				</thead>
				<tbody>
					{data.map(row => (
						<AdminTableRow
							formatFlow={formatFlow}
							formatValue={formatValue}
							key={row.agreementProcessId}
							openDrawer={openDrawer}
							row={row}
							showGuid={showGuid}
							t={t}
						/>
					))}
				</tbody>
			</table>

			{!data.length && !isLoading && (
				<div className="admin-empty">
					<Typography TagName="p">{t('admin_no_results') || 'No applications found'}</Typography>
				</div>
			)}
		</div>
	);
}
